<template>
  <div class="mx-auto max-w-5xl flex flex-col gap-4 lg:gap-6">
    <div class="flex flex-col gap-1">
      <h2 class="text-2xl font-bold tracking-tight">{{ t('user.profile.title') }}</h2>
      <p class="text-muted-foreground">{{ t('user.profile.subtitle') }}</p>
    </div>
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- Sidebar -->
      <aside class="lg:w-64">
        <Card class="py-0">
          <CardContent class="p-2">
            <nav class="flex flex-col gap-0.5">
              <Button
                variant="ghost"
                :class="['justify-start', activeSection === 'profile' && 'bg-muted']"
                @click="activeSection = 'profile'"
                data-test-id="profile-nav-profile"
              >
                <UserIcon class="size-4" />
                {{ t('user.profile.nav.profile') }}
              </Button>
              <Button
                v-if="programsRef.length > 0"
                variant="ghost"
                :class="['justify-start', activeSection === 'programs' && 'bg-muted']"
                @click="activeSection = 'programs'"
                data-test-id="profile-nav-programs"
              >
                <BookOpenIcon class="size-4" />
                {{ t('user.profile.nav.programs') }}
              </Button>
              <Button
                variant="ghost"
                :class="['justify-start', activeSection === 'password' && 'bg-muted']"
                @click="activeSection = 'password'"
                data-test-id="profile-nav-password"
              >
                <ShieldIcon class="size-4" />
                {{ t('user.profile.nav.password') }}
              </Button>
              <Button
                variant="ghost"
                :class="['justify-start', activeSection === 'privacy' && 'bg-muted']"
                @click="activeSection = 'privacy'"
                data-test-id="profile-nav-privacy"
              >
                <ScaleIcon class="size-4" />
                {{ t('user.profile.nav.privacy') }}
              </Button>
            </nav>
          </CardContent>
        </Card>
      </aside>

      <!-- Content -->
      <div class="flex-1">
        <!-- Profile Section -->
        <Card v-show="activeSection === 'profile'" data-test-id="profile-content">
          <form class="flex flex-col gap-6 px-6" @submit.prevent="submitProfile">
            <div v-if="userRef" class="flex items-center gap-4">
              <Avatar class="size-16">
                <AvatarImage v-if="userRef.picture" :src="profilePictureUrl" alt="avatar" />
                <AvatarFallback>{{ userRef.firstName?.[0] }}{{ userRef.lastName?.[0] }}</AvatarFallback>
              </Avatar>
              <Button type="button" variant="outline" @click="editProfilePicture" data-test-id="profile-avatar-edit">
                {{ t('user.profile.uploadImage') }}
              </Button>
            </div>
            <FormField v-if="userRef" v-slot="{ componentField }" name="name">
              <FormItem>
                <FormLabel>{{ t('user.profile.name') }}</FormLabel>
                <FormControl>
                  <Input v-bind="componentField" disabled />
                </FormControl>
              </FormItem>
            </FormField>
            <div v-if="userRef" class="grid gap-2">
              <p id="profile-bio-label" class="text-sm font-medium leading-none">{{ t('user.profile.bio') }}</p>
              <TiptapEditor
                v-model="descriptionRef"
                :placeholder="t('auth.editProfile.description.placeholder')"
                aria-labelledby="profile-bio-label"
                data-test-id="description-editor"
              />
            </div>

            <!-- Preferences -->
            <div class="grid gap-2">
              <Label for="profile-language">{{ t('user.profile.language.appLanguage') }}</Label>
              <Popover>
                <PopoverTrigger as-child>
                  <Button id="profile-language" variant="outline" class="justify-between" data-test-id="profile-language">
                    <div class="flex items-center gap-2">
                      <LanguagesIcon class="size-4" />
                      {{ languageOptions.find((o) => o.value === locale)?.label }}
                    </div>
                    <ChevronDownIcon class="size-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent class="w-48 p-1" align="start">
                  <Button
                    v-for="option in languageOptions"
                    :key="option.value"
                    variant="ghost"
                    class="w-full justify-start"
                    :class="locale === option.value && 'bg-muted'"
                    @click="changeLanguage(option.value)"
                  >
                    {{ option.label }}
                  </Button>
                </PopoverContent>
              </Popover>
            </div>

            <!-- Phone -->
            <div v-if="accountRef" class="grid gap-2">
              <Label for="profile-phone">{{ t('auth.profile.phone') }}</Label>
              <Input
                id="profile-phone"
                data-test-id="profile-phone"
                type="tel"
                :placeholder="t('auth.phoneModal.input.placeholder')"
                v-model="phoneInputRef"
              />
              <p v-if="phoneError" class="text-sm text-destructive">{{ phoneError }}</p>
            </div>

            <Button type="submit" :disabled="isSubmittingRef" data-test-id="profile-submit">
              {{ t('ui.button.save') }}
            </Button>
          </form>
        </Card>

        <!-- Password & Authentication Section -->
        <Card
          v-show="activeSection === 'password'"
          data-test-id="profile-password-content"
          class="flex flex-col gap-4 px-6"
        >
          <h3 class="text-lg font-semibold">{{ t('user.profile.auth.signInMethods') }}</h3>
          <Table>
            <TableBody>
              <!-- Password row -->
              <TableRow
                data-test-id="profile-password-row"
                :class="!isSsoVisible && !showPasswordForm ? 'border-b-0' : ''"
              >
                <TableCell>
                  <div class="flex items-center gap-3">
                    <KeyRoundIcon class="size-5 text-muted-foreground" />
                    <div>
                      <p class="font-medium">{{ t('auth.profile.password') }}</p>
                      <p class="text-sm text-muted-foreground">{{ t('user.profile.auth.password.configured') }}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell class="text-right">
                  <Button variant="outline" size="sm" @click="showPasswordForm = !showPasswordForm">
                    {{
                      showPasswordForm ? t('user.profile.auth.password.hide') : t('user.profile.auth.password.change')
                    }}
                  </Button>
                </TableCell>
              </TableRow>
              <!-- Password form (collapsible) -->
              <TableRow v-show="showPasswordForm">
                <TableCell colspan="2" class="py-6">
                  <form class="flex flex-col gap-4 max-w-md ml-8" @submit.prevent="submitPassword">
                    <div class="grid gap-2">
                      <Label for="current-password">{{ t('auth.password.modify.currentPassword') }}</Label>
                      <Input
                        id="current-password"
                        data-test-id="current-password"
                        type="password"
                        placeholder="********"
                        v-model="currentPasswordRef"
                      />
                    </div>
                    <div class="grid gap-2">
                      <Label for="new-password">{{ t('auth.password.modify.newPassword') }}</Label>
                      <Input
                        id="new-password"
                        data-test-id="new-password"
                        type="password"
                        placeholder="********"
                        v-model="newPasswordRef"
                      />
                      <p v-if="passwordErrors.length > 0" class="text-sm text-destructive">
                        {{ passwordErrors[0] }}
                      </p>
                    </div>
                    <div class="grid gap-2">
                      <Label for="confirm-new-password">{{ t('auth.password.modify.confirmNewPassword') }}</Label>
                      <Input
                        id="confirm-new-password"
                        data-test-id="confirm-new-password"
                        type="password"
                        placeholder="********"
                        v-model="confirmNewPasswordRef"
                      />
                    </div>
                    <Button
                      type="submit"
                      :disabled="isSubmittingRef || !currentPasswordRef || !newPasswordRef || !confirmNewPasswordRef"
                      data-test-id="password-submit"
                      class="w-fit"
                    >
                      {{ t('user.profile.auth.password.update') }}
                    </Button>
                  </form>
                </TableCell>
              </TableRow>
              <!-- SSO row -->
              <TableRow v-if="isSsoVisible" class="border-b-0">
                <TableCell colspan="2">
                  <div class="flex items-center gap-3">
                    <BuildingIcon class="size-5 text-muted-foreground" />
                    <div>
                      <p class="font-medium flex items-center gap-2">
                        {{ whiteLabelRef.companyName }}
                        <ShadcnBadge
                          v-if="accountRef?.hasSAML"
                          variant="outline"
                          class="border-green-500 text-green-600"
                          >{{ t('user.profile.auth.password.configured') }}</ShadcnBadge
                        >
                      </p>
                      <p class="text-sm text-muted-foreground">
                        {{ t('user.profile.auth.sso.description', { companyName: whiteLabelRef.companyName }) }}
                      </p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <div class="flex flex-col gap-1 mt-4">
            <h3 class="text-lg font-semibold">{{ t('user.profile.auth.twoFactor') }}</h3>
            <p class="text-sm text-muted-foreground">{{ t('user.profile.auth.twoFactor.description') }}</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead colspan="2" class="bg-muted/50 font-semibold">{{
                  t('user.profile.auth.twoFactor')
                }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow data-test-id="profile-mfa-row">
                <TableCell>
                  <div class="flex items-center gap-3">
                    <SmartphoneIcon class="size-5 text-muted-foreground" />
                    <div>
                      <p class="font-medium">{{ t('user.profile.auth.authenticatorApp') }}</p>
                      <p class="text-sm text-muted-foreground" data-test-id="mfa-status">
                        {{ accountRef?.mfaEnabled ? t('auth.mfa.status.enabled') : t('auth.mfa.status.disabled') }}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell class="text-right">
                  <Button variant="outline" size="sm" @click="toggleMfa" data-test-id="toggle-mfa">
                    {{ accountRef?.mfaEnabled ? t('auth.mfa.button.disable') : t('auth.mfa.button.enable') }}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        <!-- Privacy & Legal Section -->
        <Card
          v-show="activeSection === 'privacy'"
          data-test-id="profile-privacy-content"
          class="px-6 flex flex-col gap-4"
        >
          <div class="flex flex-col gap-1">
            <h3 class="text-lg font-semibold">{{ t('user.profile.privacy.title') }}</h3>
            <p class="text-sm text-muted-foreground">{{ t('user.profile.privacy.subtitle') }}</p>
          </div>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <div class="flex items-center gap-3">
                    <FileTextIcon class="size-5 text-muted-foreground" />
                    <div>
                      <p class="font-medium">{{ t('user.profile.privacy.cgu') }}</p>
                      <p
                        v-if="accountRef?.legalDocVersions?.cgu"
                        class="text-sm text-muted-foreground"
                        data-test-id="legal-doc-version-cgu"
                      >
                        {{
                          t('user.profile.privacy.version', { date: formatLegalDate(accountRef.legalDocVersions.cgu) })
                        }}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell class="text-right">
                  <Button variant="outline" size="sm" as-child>
                    <a
                      href="/cgu"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="!text-foreground !no-underline"
                      data-test-id="legal-doc-link-cgu"
                    >
                      {{ t('user.profile.privacy.view') }}
                    </a>
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow class="border-b-0">
                <TableCell>
                  <div class="flex items-center gap-3">
                    <FileTextIcon class="size-5 text-muted-foreground" />
                    <div>
                      <p class="font-medium">{{ t('user.profile.privacy.privacyPolicy') }}</p>
                      <p
                        v-if="accountRef?.legalDocVersions?.privacy"
                        class="text-sm text-muted-foreground"
                        data-test-id="legal-doc-version-privacy"
                      >
                        {{
                          t('user.profile.privacy.version', {
                            date: formatLegalDate(accountRef.legalDocVersions.privacy),
                          })
                        }}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell class="text-right">
                  <Button variant="outline" size="sm" as-child>
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="!text-foreground !no-underline"
                      data-test-id="legal-doc-link-privacy"
                    >
                      {{ t('user.profile.privacy.view') }}
                    </a>
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <p
            v-if="accountRef?.termsAcceptedAt"
            class="flex items-center gap-2 text-sm text-muted-foreground"
            data-test-id="legal-acceptance-status"
          >
            <CircleCheckIcon class="size-4 text-green-600" />
            {{ t('user.profile.privacy.acceptedAt', { date: formatLegalDate(accountRef.termsAcceptedAt) }) }}
          </p>
        </Card>
        <!-- Programs Section -->
        <Card
          v-show="activeSection === 'programs'"
          data-test-id="profile-programs-content"
          class="px-6 flex flex-col gap-4"
        >
          <div class="flex flex-col gap-1">
            <h3 class="text-lg font-semibold">{{ t('user.profile.programs.title') }}</h3>
            <p class="text-sm text-muted-foreground">{{ t('user.profile.programs.subtitle') }}</p>
          </div>
          <Table>
            <TableBody>
              <!-- eslint-disable-next-line vue/no-v-for-template-key -- clé sur template requise en Vue 3, un wrapper casserait le DOM du tableau -->
              <template v-for="(program, index) in programsRef" :key="program.id">
                <!-- Program row -->
                <TableRow
                  :class="expandedProgramId !== program.id && index === programsRef.length - 1 ? 'border-b-0' : ''"
                >
                  <TableCell>
                    <div class="flex items-center gap-3">
                      <BookOpenIcon class="size-5 text-muted-foreground" />
                      <p class="font-medium flex items-center gap-2">
                        {{ program.name }}
                        <ShadcnBadge
                          v-for="role in programRolesMap[program.id]"
                          :key="role"
                          variant="outline"
                          data-test-id="role-program"
                        >
                          {{ t(userRolesNamesMap[role]) }}
                        </ShadcnBadge>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell class="text-right">
                    <Button
                      v-if="
                        programRolesMap[program.id]?.includes(RoleEnumTypeV2.MENTOR) ||
                        programRolesMap[program.id]?.includes(RoleEnumTypeV2.MENTEE)
                      "
                      variant="outline"
                      size="sm"
                      @click="toggleProgram(program.id)"
                      data-test-id="profile-program-toggle-criteria"
                    >
                      {{
                        expandedProgramId === program.id
                          ? t('user.profile.programs.hideCriteria')
                          : t('user.profile.programs.show')
                      }}
                    </Button>
                  </TableCell>
                </TableRow>
                <!-- Criteria row (collapsible) -->
                <TableRow
                  v-show="expandedProgramId === program.id"
                  :class="index === programsRef.length - 1 ? 'border-b-0' : ''"
                >
                  <TableCell colspan="2" class="py-6 whitespace-normal">
                    <div class="flex flex-col gap-8 ml-8">
                      <!-- Mentor criteria -->
                      <div
                        v-if="hasMentorRole && mentorMatchingTemplateRef"
                        class="grid gap-2"
                        data-test-id="profile-mentor-criteria-section"
                      >
                        <p class="text-sm font-medium leading-none">{{
                          t('user.profile.matchingCriterias.criteriaLabel', {
                            role: t('user.profile.matchingCriterias.mentorSection'),
                          })
                        }}</p>
                        <p
                          v-if="mentorCriteriaRef && mentorCriteriaRef.length > 0"
                          class="text-sm text-muted-foreground"
                          data-test-id="profile-mentor-criteria-content"
                          v-html="fillTemplate(mentorMatchingTemplateRef || '', mentorCriteriaRef)"
                        ></p>
                        <p v-else class="text-sm text-muted-foreground">
                          {{ t('user.profile.matchingCriterias.empty') }}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          class="w-fit"
                          @click="openProgramCriteriasModal('mentor')"
                          data-test-id="profile-mentor-criteria-edit"
                        >
                          {{ t('ui.button.edit') }}
                        </Button>
                      </div>
                      <!-- Mentee criteria -->
                      <div
                        v-if="hasMenteeRole && menteeMatchingTemplateRef"
                        class="grid gap-2"
                        data-test-id="profile-mentee-criteria-section"
                      >
                        <p class="text-sm font-medium leading-none">{{
                          t('user.profile.matchingCriterias.criteriaLabel', {
                            role: t('user.profile.matchingCriterias.menteeSection'),
                          })
                        }}</p>
                        <p
                          v-if="menteeCriteriaRef && menteeCriteriaRef.length > 0"
                          class="text-sm text-muted-foreground"
                          data-test-id="profile-mentee-criteria-content"
                          v-html="fillTemplate(menteeMatchingTemplateRef || '', menteeCriteriaRef)"
                        ></p>
                        <p v-else class="text-sm text-muted-foreground">
                          {{ t('user.profile.matchingCriterias.empty') }}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          class="w-fit"
                          @click="openProgramCriteriasModal('mentee')"
                          data-test-id="profile-mentee-criteria-edit"
                        >
                          {{ t('ui.button.edit') }}
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </template>
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import type { CountryCode } from 'libphonenumber-js'
import { getCountries, isValidPhoneNumber, ParseError, parsePhoneNumberWithError } from 'libphonenumber-js'
import {
  BookOpenIcon,
  BuildingIcon,
  ChevronDownIcon,
  CircleCheckIcon,
  FileTextIcon,
  KeyRoundIcon,
  LanguagesIcon,
  ScaleIcon,
  ShieldIcon,
  SmartphoneIcon,
  UserIcon,
} from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { computed, ref, watch, watchEffect } from 'vue'
import { toast } from 'vue-sonner'
import type { ZodError } from 'zod'
import z from 'zod'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge as ShadcnBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { changePasswordMutation, updateUserMutation, V1UpdateUserDescriptionMutation } from '@/features/auth/api'
import useAccount from '@/features/auth/hooks/use-account.hook'
import usePhone from '@/features/auth/hooks/use-phone.hook'
import authStore from '@/features/auth/stores/auth/auth.store'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import TiptapEditor from '@/features/ui/components/tiptap-editor/tiptap-editor.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { getUserProgramRoles } from '@/features/users/api'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'
import { switchLanguage, userRolesNamesMap } from '@/features/users/utils'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { PROFILE_PICTURE_BUCKET_URL } from '@/utils/config/constants'
import { invalidateAccount, invalidateGroups, invalidatePhone, invalidateUser } from '@/utils/lib/vue-query'

const { t, locale, i18nGlobal } = useI18n()
const { accountRef } = useAccount()
const { phoneRef } = usePhone()

const { whiteLabelRef } = useWhiteLabel()

const activeSection = ref<'profile' | 'password' | 'programs' | 'privacy'>('profile')

// KC-526 : les versions des documents légaux sont des dates ISO (2026-05-21)
// affichées localisées (« 21 mai 2026 » / "May 21, 2026").
function formatLegalDate(isoDate: string): string {
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) return isoDate
  return parsed.toLocaleDateString(locale.value === 'en' ? 'en-US' : 'fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const accountIdRef = computed(() => accountRef.value?.id)
const { userRef } = useUser(accountIdRef)
const { programRolesRef } = useProgramRoles()
const { programsRef } = usePrograms()
const programRolesMap = ref<Record<string, RoleEnumTypeV2[]>>({})

watchEffect(async () => {
  if (!programsRef.value?.length || !accountRef.value?.id) return
  for (const program of programsRef.value) {
    if (!programRolesMap.value[program.id]) {
      try {
        const result = await getUserProgramRoles({ programId: program.id, userId: accountRef.value.id })
        programRolesMap.value[program.id] = result.roles
      } catch (error) {
        console.error(`Failed to fetch roles for program ${program.id}:`, error)
      }
    }
  }
})

const profilePictureUrl = computed(() => PROFILE_PICTURE_BUCKET_URL + userRef.value?.picture)

const descriptionRef = ref<string>('')
const phoneInputRef = ref('')
const phoneError = ref('')
const isSubmittingRef = ref(false)
const showPasswordForm = ref(false)
const expandedProgramId = ref<string | null>(null)
const isSsoVisible = computed(() => whiteLabelRef.value.samlLoginEnabled === 'true')

// Password fields
const currentPasswordRef = ref('')
const newPasswordRef = ref('')
const confirmNewPasswordRef = ref('')

const passwordSchema = z
  .string({
    invalid_type_error: 'Le mot de passe est requis',
    required_error: 'Le mot de passe est requis',
  })
  .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
  .regex(/(?=.*[a-z])/, 'Le mot de passe doit contenir au moins une lettre minuscule')
  .regex(/(?=.*[A-Z])/, 'Le mot de passe doit contenir au moins une lettre majuscule')
  .regex(/(?=.*[0-9])/, 'Le mot de passe doit contenir au moins un chiffre')
  .regex(/(?=.*[@$!%*?&\-_#])/, 'Le mot de passe doit contenir au moins un caractère spécial (@$!%*?&-_#)')

const passwordValidationSchema = z
  .object({
    currentPassword: z.string(),
    newPassword: passwordSchema,
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Les mots de passe ne correspondent pas',
  })

const passwordErrors = computed(() => {
  if (!newPasswordRef.value) return []
  const result = passwordSchema.safeParse(newPasswordRef.value)
  if (!result.success) {
    return result.error.issues.map((issue) => issue.message)
  }
  return []
})

const { mutate: changePassword } = useMutation({
  mutationFn: changePasswordMutation,
  onError: () => {
    toast.error(t('error.common'))
  },
  onSuccess: () => {
    toast.success(t('auth.password.modify.success'))
    currentPasswordRef.value = ''
    newPasswordRef.value = ''
    confirmNewPasswordRef.value = ''
    showPasswordForm.value = false
  },
})

const { setFieldValue } = useForm({
  initialValues: {
    name: '',
  },
})

watch(
  userRef,
  (user) => {
    if (user) {
      setFieldValue('name', `${user.firstName} ${user.lastName}`)
      descriptionRef.value = user.description || ''
    }
  },
  { immediate: true },
)

watch(
  phoneRef,
  (phone) => {
    if (phone && !phoneInputRef.value) {
      phoneInputRef.value = phone
    }
  },
  { immediate: true },
)

// Matching criterias
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
useProgram(selectedProgramIdRef)
useMatchingSettings(selectedProgramIdRef)
const { mentorMatchingTemplateRef, menteeMatchingTemplateRef, mentorCriteriaRef, menteeCriteriaRef, fillTemplate } =
  useMatchingInformation({
    onSubmitCallback: () => null,
  })

const hasMentorRole = computed(() => programRolesRef.value?.includes(RoleEnumTypeV2.MENTOR))
const hasMenteeRole = computed(() => programRolesRef.value?.includes(RoleEnumTypeV2.MENTEE))

const openProgramCriteriasModal = (role: 'mentor' | 'mentee') => {
  modalsStore.updateModalAttachedData('program-criterias', { role })
  modalsStore.toggleModal('program-criterias', true)
}

function toggleProgram(programId: string) {
  if (expandedProgramId.value === programId) {
    expandedProgramId.value = null
  } else {
    expandedProgramId.value = programId
    programsStore.setSelectedProgramId(programId)
  }
}

function editProfilePicture() {
  modalsStore.toggleModal('profile-picture', true)
}

function normalizePhoneNumber(phoneNumberInput: string): string | null {
  if (phoneNumberInput.startsWith('07') || phoneNumberInput.startsWith('06')) {
    try {
      const phoneNumber = parsePhoneNumberWithError(phoneNumberInput, 'FR' as CountryCode)
      if (phoneNumber.isValid()) return phoneNumber.format('E.164')
    } catch {
      /* continue */
    }
  }
  try {
    const phoneNumber = parsePhoneNumberWithError(phoneNumberInput)
    if (phoneNumber.isValid()) return phoneNumber.format('E.164')
  } catch (error) {
    if (!(error instanceof ParseError)) return null
  }
  const supportedCountries = getCountries()
  for (const country of supportedCountries) {
    try {
      const phoneNumber = parsePhoneNumberWithError(phoneNumberInput, country as CountryCode)
      if (phoneNumber.isValid()) return phoneNumber.format('E.164')
    } catch (error) {
      if (!(error instanceof ParseError)) break
      continue
    }
  }
  return null
}

function toggleMfa() {
  if (accountRef.value?.mfaEnabled) {
    modalsStore.toggleModal('mfa-disable', true)
  } else {
    modalsStore.toggleModal('mfa-setup', true)
  }
}

async function submitProfile() {
  try {
    isSubmittingRef.value = true
    phoneError.value = ''

    // Save description
    await V1UpdateUserDescriptionMutation({
      userId: accountRef.value!.id!,
      description: descriptionRef.value,
    })

    // Save phone if changed
    if (phoneInputRef.value && phoneInputRef.value !== phoneRef.value) {
      const normalized = normalizePhoneNumber(phoneInputRef.value)
      if (!normalized || !isValidPhoneNumber(normalized)) {
        phoneError.value = t('auth.phoneModal.error.invalid')
        return
      }
      await updateUserMutation({
        phone: normalized,
        userId: accountRef.value!.id!,
      })
      await invalidatePhone()
    }

    await invalidateUser(accountRef.value!.id!)
    await invalidateAccount()
    await invalidateGroups(selectedProgramIdRef.value!)
    toast.success(t('auth.editProfile.success'))
  } catch {
    toast.error(t('error.common'))
  } finally {
    isSubmittingRef.value = false
  }
}

async function submitPassword() {
  try {
    isSubmittingRef.value = true
    const passwordData = await passwordValidationSchema.parseAsync({
      currentPassword: currentPasswordRef.value,
      newPassword: newPasswordRef.value,
      confirmNewPassword: confirmNewPasswordRef.value,
    })
    changePassword({
      userId: accountRef.value!.id!,
      oldPassword: passwordData.currentPassword,
      password: passwordData.newPassword,
      confirmPassword: passwordData.confirmNewPassword,
    })
  } catch (e: any) {
    if (e.errors) {
      const error: ZodError = e
      error.errors.forEach((err) => toast.error(err.message))
    } else {
      toast.error(t('error.common'))
    }
  } finally {
    isSubmittingRef.value = false
  }
}

const languageOptions = computed(() => [
  { label: t('ui.select.lang.fr'), value: 'fr' },
  { label: t('ui.select.lang.en'), value: 'en' },
])

const { mutate: updateUser } = useMutation({
  mutationFn: updateUserMutation,
  onError: () => {
    toast.error(t('error.common'))
  },
  onSuccess: async () => {
    toast.success(t('auth.language.success'))
  },
})

async function changeLanguage(value: string) {
  if (!i18nGlobal) {
    console.error('Global i18n instance not available')
    return
  }
  await switchLanguage(value, i18nGlobal)
  try {
    updateUser({
      language: value,
      userId: accountRef.value!.id!,
    })
  } catch (e: any) {
    const error: ZodError = e
    error.errors.forEach((err) => {
      toast.error(err.message)
    })
  }
  await authStore.setUserLanguage(value)
}
</script>
